// useAppFolioRefresh — the lease builder's AppFolio refresh (WEL-169).
//
// On opening a draft it refreshes the household's AppFolio property ONCE in the
// background — never blocking the page — and, only when the mirror actually
// changed, reloads the group so the preview and the Review summary pick up the
// new values. `refreshNow` backs the "Refresh from AppFolio" button for right
// after staff fix a field in AppFolio. The rules live in lib/appfolioRefresh.

import { useCallback, useEffect, useRef, useState } from 'react';
import { refreshFromAppFolio } from '@/lib/leaseDraftsClient';
import {
  backgroundRefreshDue,
  runAppFolioRefresh,
  type AppFolioRefreshMode,
} from '@/lib/appfolioRefresh';

export interface AppFolioRefreshState {
  busy: boolean;
  note: string | null;
  error: string | null;
  /** Bumps each time a refresh changed the mirror (drives dependent refetches). */
  changeCount: number;
  refreshNow: () => void;
}

export function useAppFolioRefresh(
  groupId: string | undefined,
  opts: { enabled: boolean; onChanged: () => Promise<unknown> },
): AppFolioRefreshState {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [changeCount, setChangeCount] = useState(0);
  const backgroundRanFor = useRef<string | null>(null);
  const onChangedRef = useRef(opts.onChanged);
  onChangedRef.current = opts.onChanged;
  const inFlight = useRef(false);

  const run = useCallback(
    async (mode: AppFolioRefreshMode) => {
      if (!groupId || inFlight.current) return;
      inFlight.current = true;
      if (mode === 'manual') {
        setBusy(true);
        setError(null);
        setNote(null);
      }
      try {
        const out = await runAppFolioRefresh({
          call: () => refreshFromAppFolio(groupId),
          onChanged: () => onChangedRef.current(),
          mode,
        });
        if (out.reloaded) setChangeCount((n) => n + 1);
        if (out.note) setNote(out.note);
        if (out.error) setError(out.error);
      } finally {
        inFlight.current = false;
        if (mode === 'manual') setBusy(false);
      }
    },
    [groupId],
  );

  // Once per draft, as soon as it has loaded — in the background.
  useEffect(() => {
    if (!backgroundRefreshDue(groupId, opts.enabled, backgroundRanFor.current)) return;
    backgroundRanFor.current = groupId!;
    void run('background');
  }, [groupId, opts.enabled, run]);

  const refreshNow = useCallback(() => void run('manual'), [run]);
  return { busy, note, error, changeCount, refreshNow };
}
